import type { DeviceFormFactor } from "@recital-master/shared";

export type WebDeviceSignals = {
  userAgent: string;
  platform: string;
  mobile: boolean | null;
  maxTouchPoints: number;
};

export type WebDeviceDescription = {
  formFactor: DeviceFormFactor;
  operatingSystem: string;
  modelCode: null;
  manufacturer: null;
  browserName: string;
};

/** Expo documents that Android's osName may contain a full build fingerprint.
 * Normalize native values to the OS-family contract used by device records. */
export function describeNativeOperatingSystem(
  platform: "ios" | "android",
  osName: string | null
): string {
  if (platform === "android") return "Android";
  const trimmed = osName?.trim();
  return trimmed ? trimmed.slice(0, 40).trim() : "iOS";
}

function browserName(userAgent: string): string {
  if (/Edg(?:A|iOS)?\//i.test(userAgent)) return "Edge";
  if (/CriOS|Chrome\//i.test(userAgent)) return "Chrome";
  if (/FxiOS|Firefox\//i.test(userAgent)) return "Firefox";
  if (/Safari\//i.test(userAgent)) return "Safari";
  return "Browser";
}

/** Browser-safe classification. Exact hardware models are intentionally not
 * required because modern browsers reduce that fingerprinting information. */
export function describeWebDevice(signals: WebDeviceSignals): WebDeviceDescription {
  const ua = signals.userAgent;
  const platform = signals.platform;
  const browser = browserName(ua);
  const iPad = /iPad/i.test(ua) || (/Mac/i.test(platform) && signals.maxTouchPoints > 1);
  const iPhone = /iPhone|iPod/i.test(ua);
  const android = /Android/i.test(ua) || /Android/i.test(platform);

  if (iPad) {
    return {
      formFactor: "tablet",
      operatingSystem: "iPadOS",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  if (iPhone) {
    return {
      formFactor: "phone",
      operatingSystem: "iOS",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  if (android) {
    const phone = signals.mobile ?? /Mobile/i.test(ua);
    return {
      formFactor: phone ? "phone" : "tablet",
      operatingSystem: "Android",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  if (/CrOS/i.test(ua)) {
    return {
      formFactor: "computer",
      operatingSystem: "ChromeOS",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  if (/Win/i.test(platform) || /Windows/i.test(ua)) {
    return {
      formFactor: "computer",
      operatingSystem: "Windows",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) {
    return {
      formFactor: "computer",
      operatingSystem: "macOS",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  if (/Linux/i.test(platform) || /Linux/i.test(ua)) {
    return {
      formFactor: "computer",
      operatingSystem: "Linux",
      modelCode: null,
      manufacturer: null,
      browserName: browser
    };
  }
  return {
    formFactor: "computer",
    operatingSystem: "Web",
    modelCode: null,
    manufacturer: null,
    browserName: browser
  };
}
